import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_MS = 5000;

export const HeroBanner = () => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API_URL}/api/banner`)
      .then((res) => setSlides(res.data.slides || []))
      .catch(() => {});
  }, []);

  const next = useCallback(() => setIdx((i) => (i + 1) % slides.length), [slides.length]);
  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (slides.length < 2 || paused) return undefined;
    const timer = setInterval(next, AUTO_MS);
    return () => clearInterval(timer);
  }, [slides.length, paused, next]);

  if (slides.length === 0) return null;

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(delta) > 50) (delta < 0 ? next() : prev());
    touchX.current = null;
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10"
      data-testid="home-banner"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative overflow-hidden rounded-3xl border border-theme shadow-xl">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <Slide key={slide.id || i} slide={slide} index={i} t={t} />
          ))}
        </div>

        {slides.length > 1 && (
          <>
            <button
              onClick={prev}
              data-testid="banner-prev"
              aria-label="Précédent"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 backdrop-blur-sm text-[color:var(--color-primary)] shadow hover:bg-white transition-colors rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              data-testid="banner-next"
              aria-label="Suivant"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 backdrop-blur-sm text-[color:var(--color-primary)] shadow hover:bg-white transition-colors rounded-full"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {slides.map((s, i) => (
                <button
                  key={s.id || i}
                  onClick={() => setIdx(i)}
                  data-testid={`banner-dot-${i}`}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? 'w-6 bg-[color:var(--color-primary)]' : 'w-1.5 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Slide = ({ slide, index, t }) => {
  const isProduct = slide.type === 'product' && slide.product;
  const image = slide.image_url || (isProduct ? slide.product.image_url : null);
  const title = slide.title || (isProduct ? slide.product.name : '');
  const subtitle = slide.subtitle || (isProduct ? slide.product.brand : '');
  const ctaLink = isProduct ? `/produit/${slide.product.id}` : slide.cta_link;
  const ctaLabel = slide.cta_label || (isProduct ? t('banner_discover') : null);
  const isExternal = ctaLink && /^https?:\/\//i.test(ctaLink);

  const content = (
    <div className="grid grid-cols-1 sm:grid-cols-12 h-full">
      <div className="sm:col-span-6 bg-[color:var(--color-primary)] on-color text-white px-12 py-7 sm:py-10 sm:pl-14 sm:pr-10 lg:p-12 flex flex-col justify-center relative overflow-hidden order-2 sm:order-1">
        <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-[color:var(--color-emerald)] opacity-25 blur-2xl pointer-events-none" />
        <div className="absolute -left-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-xl pointer-events-none" />
        <div className="relative z-10">
          {isProduct && subtitle && (
            <p className="text-white/70 text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{subtitle}</p>
          )}
          <h3 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {title}
          </h3>
          {!isProduct && subtitle && (
            <p className="text-white/80 text-sm sm:text-base mb-4 line-clamp-2">{subtitle}</p>
          )}
          {ctaLink && ctaLabel && (
            <span className="inline-flex items-center gap-2 bg-white text-[color:var(--color-primary)] text-xs sm:text-sm font-bold rounded-full px-5 py-2.5 w-fit mt-2 shadow-lg hover:translate-x-1 transition-transform">
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </div>
      </div>
      <div className={`sm:col-span-6 bg-sand relative min-h-[180px] sm:min-h-0 order-1 sm:order-2 overflow-hidden ${image ? '' : 'hidden sm:block'}`}>
        {image ? (
          <img
            src={image}
            alt={title}
            loading={index === 0 ? 'eager' : 'lazy'}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ borderRadius: 0 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[color:var(--color-primary)] opacity-20 text-8xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Ô</span>
          </div>
        )}
      </div>
    </div>
  );

  const cls = 'relative flex-shrink-0 w-full h-auto sm:h-80 block';
  if (ctaLink && isExternal) {
    return (
      <a href={ctaLink} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`banner-slide-${index}`}>
        {content}
      </a>
    );
  }
  if (ctaLink) {
    return (
      <Link to={ctaLink} className={cls} data-testid={`banner-slide-${index}`}>
        {content}
      </Link>
    );
  }
  return <div className={cls} data-testid={`banner-slide-${index}`}>{content}</div>;
};

export default HeroBanner;
